import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, FileText, Building2, Copyright, Award, Users, TrendingUp, Star, ChevronDown, ChevronUp, Check } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import ContactModal from '../components/ContactModal';
import SEO from '../components/SEO';
import MobileSlider from '../components/MobileSlider';
import PricingCard from '../components/PricingCard';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const Home = ({ isLandingPage = false }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeFaq, setActiveFaq] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emblaRef] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: 5000, stopOnInteraction: false })]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    {
      icon: Shield,
      title: 'Trademark Registration',
      description: 'Secure your brand name, logo, and slogans with comprehensive trademark protection services across the United States.',
      link: '/services/trademark/register',
      image: 'https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGRvY3VtZW50c3xlbnwwfHx8fDE3NjQwMjUzMjh8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Copyright,
      title: 'Copyright Protection',
      description: 'Protect your creative works including books, music, art, software, and digital content with official copyright registration.',
      link: '/services/copyright/register',
      image: 'https://images.unsplash.com/photo-1768464706302-4876d5be12f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMGFydGlzdCUyMGNvcHlyaWdodCUyMGFydHdvcmslMjBzdHVkaW98ZW58MHx8fHwxNzc1NzYyNjI4fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Award,
      title: 'Patent Services',
      description: 'Secure your innovations with expert patent application services including utility patents, design patents, and provisional applications.',
      link: '/services/patent/application',
      image: 'https://images.unsplash.com/photo-1668511238537-bb2d2c1e005f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHx0ZWNobm9sb2d5JTIwaW5ub3ZhdGlvbiUyMHBhdGVudCUyMGxhYm9yYXRvcnl8ZW58MHx8fHwxNzc1NzYyNjI4fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Building2,
      title: 'Company Formation',
      description: 'Start your business with proper legal structure. Form your LLC, C Corp, S Corp, or Non-Profit with expert guidance.',
      link: '/services/company/llc',
      image: 'https://images.unsplash.com/photo-1632923943930-d2f53d09313a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHN0YXJ0dXAlMjBvZmZpY2UlMjBmb3JtYXRpb258ZW58MHx8fHwxNzc1NzYyNjI4fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: FileText,
      title: 'Legal Services',
      description: 'Comprehensive legal support including business immigration, commercial law, data protection, and partnership consultations.',
      link: '/services/legal/immigration',
      image: 'https://images.unsplash.com/photo-1528747008803-f9f5cc8f1a64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBhdHRvcm5leXxlbnwwfHx8fDE3NjQwMjUzMzR8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Users,
      title: 'Additional Services',
      description: 'Amazon Brand Registry, bookkeeping, tax advisory, and comprehensive corporate services for growing businesses.',
      link: '/services/additional/amazon-brand-registry',
      image: 'https://images.unsplash.com/photo-1758691736493-aa6d22c0f8a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZyUyMHByb2Zlc3Npb25hbCUyMGJvYXJkcm9vbXxlbnwwfHx8fDE3NzU3NjI2MzV8MA&ixlib=rb-4.1.0&q=85'
    },
  ];

  const benefits = [
    {
      icon: Shield,
      title: 'Legal Protection',
      description: 'Gain exclusive nationwide rights and legal presumption of ownership with federal registration.'
    },
    {
      icon: TrendingUp,
      title: 'Brand Value',
      description: 'Increase your brand\'s value and credibility with official trademark registration status.'
    },
    {
      icon: Award,
      title: 'Market Exclusivity',
      description: 'Prevent competitors from using confusingly similar marks in your industry and market.'
    },
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Submit Your Details',
      description: 'Provide us with your brand name, logo, and basic business information through our secure online form.'
    },
    {
      number: '02',
      title: 'We Handle the Filing',
      description: 'Our experts conduct searches, prepare your application, and file it with the appropriate office accurately and efficiently.'
    },
    {
      number: '03',
      title: 'Get Official Protection',
      description: 'Once approved, your intellectual property is officially registered, giving you exclusive legal rights and nationwide protection.'
    },
  ];

  const whyChooseUs = [
    {
      icon: Shield,
      title: 'Expert IP Attorneys',
      description: 'Expert legal team with decades of intellectual property experience. We protect your brand with precision and care.'
    },
    {
      icon: TrendingUp,
      title: 'Transparent Pricing',
      description: 'Clear, upfront pricing with no hidden fees. Comprehensive packages designed to fit your business needs.'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Full-service support from start to finish. Our team is available via phone, email, and live chat.'
    },
    {
      icon: Award,
      title: 'Proven Track Record',
      description: 'Over 10,000 successful registrations with a 98% approval rate. Trusted by businesses nationwide.'
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Mitchell',
      role: 'CEO, TechNova Solutions',
      image: 'https://randomuser.me/api/portraits/women/44.jpg',
      text: 'Federal Trademark Experts made our brand registration process seamless. Their attention to detail and expert guidance gave us complete confidence in protecting our intellectual property. Highly recommend their services!'
    },
    {
      name: 'Marcus Johnson',
      role: 'Founder, Urban Threads Apparel',
      image: 'https://randomuser.me/api/portraits/men/32.jpg',
      text: 'As an Amazon seller, protecting my brand was crucial. The team handled everything from trademark registration to Brand Registry enrollment. Professional, fast, and affordable!'
    },
    {
      name: 'David Chen',
      role: 'CTO, Innovate Labs',
      image: 'https://randomuser.me/api/portraits/men/67.jpg',
      text: 'Their patent services exceeded our expectations. The attorneys are knowledgeable, responsive, and truly care about protecting your innovations. We\'ve secured three patents with their help!'
    },
    {
      name: 'Jennifer Rodriguez',
      role: 'Author & Publisher',
      image: 'https://randomuser.me/api/portraits/women/68.jpg',
      text: 'I needed copyright protection for my books and Federal Trademark Experts delivered exceptional service. Fast, affordable, and thorough. I now have peace of mind knowing my work is protected.'
    },
    {
      name: 'Robert Thompson',
      role: 'Restaurant Owner',
      image: 'https://randomuser.me/api/portraits/men/85.jpg',
      text: 'From trademark search to final registration, Federal Trademark Experts provided outstanding service. Their detailed search report gave me confidence before investing in my brand. Worth every penny!'
    },
  ];

  const faqs = [
    {
      question: 'What is a trademark and why do I need one?',
      answer: 'A trademark is a unique symbol, word, phrase, or design that identifies and distinguishes your products or services from others. Registering your trademark provides exclusive legal rights to use the mark in commerce and prevents others from using similar marks that could confuse consumers. It\'s essential for protecting your brand identity and building brand recognition.'
    },
    {
      question: 'How long does the trademark registration process take?',
      answer: 'The typical trademark registration process takes 8-12 months from filing to registration. This includes the initial USPTO examination (3-4 months), publication for opposition (30 days), and final registration. With our expedited service, we can file your application within 24 hours of receiving your information.'
    },
    {
      question: 'What are USPTO fees and are they included in your prices?',
      answer: 'USPTO (United States Patent and Trademark Office) filing fees are government fees required to process your trademark application. These fees are separate from our service fees and are noted as "+ Federal Filing Fee" in our pricing. Current USPTO fees range from $250-$350 per class of goods/services.'
    },
    {
      question: 'Can I register a trademark for multiple classes?',
      answer: 'Yes, you can register a trademark for multiple classes of goods and services. Each class requires a separate filing fee. Our Multi-Class and Premium packages include coverage for multiple classes, providing comprehensive protection across different product or service categories.'
    },
    {
      question: 'What happens if my trademark application is rejected?',
      answer: 'If the USPTO issues an office action (objection), our attorneys will work with you to prepare a comprehensive response. Our Standard and higher packages include office action support. We have a 98% approval rate because we conduct thorough searches and prepare applications carefully to minimize rejections.'
    },
    {
      question: 'Do I need a trademark search before filing?',
      answer: 'Yes, a trademark search is highly recommended before filing. It identifies potential conflicts with existing marks and helps avoid costly rejections. All our packages include trademark searches - from basic searches in our Essentials package to comprehensive 50-state searches in our Professional package.'
    },
    {
      question: 'How long does trademark protection last?',
      answer: 'Once registered, a trademark lasts for 10 years and can be renewed indefinitely as long as you continue using the mark in commerce. You must file maintenance documents between the 5th and 6th year, and then renew every 10 years thereafter.'
    },
    {
      question: 'What\'s the difference between ™ and ® symbols?',
      answer: 'The ™ symbol can be used with any mark to indicate trademark rights, even without registration. The ® symbol can only be used after your trademark is officially registered with the USPTO. Using ® before registration is illegal and can result in penalties.'
    },
    {
      question: 'Can I trademark a business name that\'s already registered as an LLC?',
      answer: 'State LLC registration and federal trademark registration are separate processes. Just because a business name is registered as an LLC in your state doesn\'t mean you have trademark rights nationwide. A federal trademark registration provides much broader protection and prevents others from using similar marks across the United States.'
    },
    {
      question: 'What is your 100% Approval Guarantee?',
      answer: 'Our 100% Approval Guarantee (included in Standard and higher packages) means that if your trademark application is rejected by the USPTO, we will work with you to overcome the rejection at no additional attorney fee. If we determine the mark cannot be registered, we will provide a full refund of our service fees (USPTO fees are non-refundable).'
    },
  ];

  const pricingPackages = [
    {
      name: 'Essentials',
      price: '$45',
      period: '+ Federal Filing Fee',
      description: 'Perfect for first-time trademark filers',
      features: [
        'Case Review & Assessment',
        'Basic Trademark Search',
        'USPTO Filing Assistance',
        'Trademark Monitoring (1 Month)',
        'Email Support',
        'Attorney Pre-Filing Letter',
        'Trademark Secured'
      ]
    },
    {
      name: 'Standard',
      price: '$149',
      period: '+ Federal Filing Fee',
      description: 'Ideal for growing businesses',
      features: [
        'Everything in Essentials',
        'Expert Case Review',
        'Comprehensive Trademark Search',
        'Trademark Monitoring (3 Months)',
        'Risk Evaluation Report',
        'Cease & Desist Template',
        'Dedicated Account Manager',
        '100% Approval Guarantee'
      ],
      popular: true
    },
    {
      name: 'Professional',
      price: '$299',
      period: '+ Federal Filing Fee',
      description: 'Complete protection for established brands',
      features: [
        'Everything in Standard',
        'Attorney Review & Filing',
        '24-Hour Expedited Processing',
        'Trademark Monitoring (12 Months)',
        'Comprehensive Search (50 States)',
        'Office Action Support',
        'Opposition & Litigation Advice',
        '100% Approval Guarantee'
      ]
    },
    {
      name: 'Premium',
      price: '$899',
      period: '+ Federal Filing Fee',
      description: 'Maximum protection & premium service',
      features: [
        'Everything in Professional',
        'Direct Attorney Assignment',
        'Trademark Monitoring (24 Months)',
        'International Trademark Support',
        '24/7 Secure Portal Access',
        'Complete Opposition Management',
        'White Glove Service',
        '100% Approval Guarantee'
      ]
    },
  ];

  const stats = [
    { number: '10,000+', label: 'Trademarks Registered' },
    { number: '98%', label: 'Approval Success Rate' },
    { number: '50', label: 'States Covered' },
    { number: '24/7', label: 'Expert Support' },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title={isLandingPage ? "Register Your Trademark Today" : "Trademark Registration, Copyright & Patent Services"}
        description="Federal Trademark Experts offers affordable trademark registration starting at $45, copyright protection, patent filing, and LLC formation across the United States."
        path={isLandingPage ? "/lp" : "/"}
        keywords="trademark registration, copyright protection, patent filing, LLC formation, intellectual property, USPTO"
      />
      {/* 1. Hero Section - Enhanced Design */}
      <section className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 text-white py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsYXclMjBvZmZpY2UlMjBjb25mZXJlbmNlJTIwcm9vbSUyMGRhcmt8ZW58MHx8fHwxNzc1NzYyNjM1fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-500 rounded-full opacity-5 hidden md:block"></div>
        <div className="absolute bottom-20 left-10 w-72 h-72 bg-indigo-500 rounded-full opacity-5 hidden md:block"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center min-w-0">
            <div className="md:hidden min-w-0">
              <ContactForm title="Register Your Trademark — Free Consultation" buttonText="Get Started" />
            </div>

            <div>
              <div className="inline-flex items-center bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-4 py-2 mb-6">
                <Shield className="h-4 w-4 text-blue-300 mr-2" />
                <span className="text-sm font-semibold text-blue-200">Trusted by 10,000+ Businesses</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
                Protect Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">Business Identity</span> with Expert Legal Support
              </h1>
              <p className="text-base sm:text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
                Secure your brand name, logo, or slogan with Federal Trademark Experts — your reliable partner for fast and accurate trademark filings across the United States.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="group relative inline-flex items-center justify-center bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 bg-size-200 hover:bg-pos-100 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:shadow-[0_20px_50px_-10px_rgba(59,130,246,0.7)] transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  <span className="relative">Register My Trademark</span>
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
                </button>
              </div>
            </div>

            <div className="hidden md:block">
              <ContactForm title="Register Your Trademark — Free Consultation" buttonText="Get Started" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. Why You Need Trademark - Benefits */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-blue-400/5 rounded-full hidden md:block"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-400/5 rounded-full hidden md:block"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-bold px-6 py-2.5 rounded-full mb-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <Check className="h-4 w-4 mr-2" />
              TRADEMARK BENEFITS
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
              Why You Need <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 bg-size-200 animate-gradient">Trademark Registration</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Protect your brand identity and secure your competitive advantage with comprehensive trademark protection.
            </p>
          </div>
          
          <MobileSlider enabled={isMobile}>
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="group relative bg-white rounded-2xl shadow-xl p-8 h-full transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(59,130,246,0.6)] hover:-translate-y-3 border-2 border-transparent hover:border-blue-400 overflow-hidden">
                  {/* Background gradient glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Animated corner accent */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-bl-full transform scale-0 group-hover:scale-100 transition-transform duration-500"></div>
                  
                  {/* Icon container with enhanced styling */}
                  <div className="relative mb-6 inline-flex">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl blur-md opacity-50 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="relative bg-gradient-to-br from-blue-500 via-cyan-500 to-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                      <Icon className="h-10 w-10 text-white group-hover:scale-110 transition-transform duration-300" />
                      {/* Shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/40 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
                    </div>
                  </div>
                  
                  <div className="relative">
                    <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-cyan-600 transition-all duration-300">{benefit.title}</h3>
                    <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-300">{benefit.description}</p>
                  </div>
                  
                  {/* Bottom gradient line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              );
            })}
          </MobileSlider>
        </div>
      </section>

      {/* 3. How We Work - Enhanced */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100 rounded-full opacity-10 -translate-y-1/2 translate-x-1/2 hidden md:block"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-2 rounded-full mb-4">
              SIMPLE PROCESS
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">
              How We Work: <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">3 Easy Steps</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Getting your intellectual property protected has never been easier. Our streamlined process saves you time and eliminates confusion.
            </p>
          </div>
          
          <MobileSlider enabled={isMobile}>
            {processSteps.map((step, index) => (
              <div key={index} className="relative group">
                <div className="bg-white rounded-2xl shadow-xl p-8 h-full border-2 border-transparent hover:border-blue-500 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-600 transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">{step.description}</p>
                  </div>
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-tl-full opacity-50"></div>
                </div>
              </div>
            ))}
          </MobileSlider>
          
          <div className="text-center mt-12">
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-md transition-all duration-200"
            >
              Start Your Application
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* 4. Why Choose Us - Enhanced Dark Theme */}
      <section className="py-16 md:py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 via-indigo-900/50 to-slate-900"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500 rounded-full opacity-5"></div>
          <div className="absolute bottom-20 right-10 w-64 h-64 bg-cyan-500 rounded-full opacity-5"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 text-blue-300 text-sm font-semibold px-4 py-2 rounded-full mb-4">
              WHY US
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
              Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">Federal Trademark Experts</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Your trusted partner for intellectual property protection with proven expertise and dedicated support.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="group">
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 transition-all duration-300 hover:bg-white/15 hover:border-white/30 hover:-translate-y-1">
                    <div className="bg-gradient-to-br from-blue-500 to-cyan-500 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                    <p className="text-blue-200">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. Services Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Comprehensive Brand Protection Services
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We offer a complete range of intellectual property and business formation solutions tailored to meet your goals.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="group relative bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(59,130,246,0.5)] hover:-translate-y-3 border border-slate-200 hover:border-blue-400">
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-cyan-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="h-48 overflow-hidden relative">
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-blue-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"></div>
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover transition-all duration-700 group-hover:scale-125 group-hover:brightness-110"
                    />
                  </div>
                  
                  <div className="p-6 relative z-20">
                    {/* Icon with enhanced animations */}
                    <div className="absolute -top-10 left-6 bg-gradient-to-br from-blue-500 via-cyan-500 to-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl transform group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 group-hover:shadow-[0_20px_40px_-10px_rgba(59,130,246,0.8)]">
                      <Icon className="h-9 w-9 text-white group-hover:scale-110 transition-transform duration-300" />
                      {/* Shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/40 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
                    </div>
                    
                    <div className="mt-8">
                      <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-cyan-600 transition-all duration-300">{service.title}</h3>
                      <p className="text-slate-600 mb-5 leading-relaxed group-hover:text-slate-700 transition-colors duration-300">{service.description}</p>
                      <div className="flex items-center justify-between">
                        {!isLandingPage ? (
                          <Link
                            to={service.link}
                            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold group/link transition-all duration-300"
                          >
                            Learn More
                            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 group-hover:text-cyan-500 transition-all duration-300" />
                          </Link>
                        ) : (
                          <button
                            onClick={() => setIsModalOpen(true)}
                            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-all duration-300"
                          >
                            Get Started
                            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 group-hover:text-cyan-500 transition-all duration-300" />
                          </button>
                        )}
                        {/* Animated corner decoration */}
                        <div className="w-8 h-8 border-r-2 border-b-2 border-blue-500 opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-150"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom gradient bar */}
                  <div className="h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. Pricing Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Pick Your Trademark Package
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Choose the package that best fits your needs. All packages include expert support and guidance throughout the process.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {pricingPackages.map((pkg, index) => (
              <PricingCard key={index} {...pkg} onCtaClick={() => setIsModalOpen(true)} />
            ))}
          </div>
        </div>
      </section>

      {/* 7. CTA - Enhanced */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Trusted by businesses nationwide for reliable intellectual property protection.
            </p>
          </div>
          
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.33%] min-w-0 pl-4">
                  <div className="bg-white rounded-lg shadow-lg p-8 h-full transition-all duration-300 hover:shadow-xl mr-4">
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <p className="text-slate-700 mb-6 italic">"{testimonial.text}"</p>
                    <div className="flex items-center">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover mr-4"
                      />
                      <div>
                        <div className="font-bold text-slate-900">{testimonial.name}</div>
                        <div className="text-slate-600 text-sm">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 8. Testimonials - 5 with Auto Slider */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-600">
              Got questions? Find quick answers to common trademark concerns here.
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-white hover:bg-slate-50 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                  {activeFaq === index ? (
                    <ChevronUp className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {activeFaq === index && (
                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                    <p className="text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQs - 10 Questions */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-800 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full opacity-10 hidden md:block"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full opacity-10 hidden md:block"></div>
        </div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full mb-8 border-2 border-white/30">
            <Shield className="h-10 w-10 text-blue-300" />
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6">
            Don't Risk Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300">Brand's Future!</span>
          </h2>
          <p className="text-xl md:text-2xl text-blue-100 mb-10 leading-relaxed max-w-3xl mx-auto">
            Unregistered intellectual property can lead to costly legal battles. Protect your brand with Federal Trademark Experts's expert services—fast, reliable, and hassle-free.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button
              onClick={() => setIsModalOpen(true)}
              className="group inline-flex items-center justify-center bg-white text-blue-900 hover:bg-blue-50 font-bold py-4 px-10 rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1"
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => { if (window.LiveChatWidget) window.LiveChatWidget.call('maximize'); }}
              className="group inline-flex items-center justify-center bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 text-white font-bold py-4 px-10 rounded-xl transition-all duration-300"
            >
              Live Chat
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8 text-blue-200">
            <div className="flex items-center">
              <Check className="h-5 w-5 mr-2" />
              <span className="text-sm font-semibold">98% Approval Rate</span>
            </div>
            <div className="flex items-center">
              <Check className="h-5 w-5 mr-2" />
              <span className="text-sm font-semibold">10,000+ Trademarks</span>
            </div>
            <div className="flex items-center">
              <Check className="h-5 w-5 mr-2" />
              <span className="text-sm font-semibold">Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title="Get Started with Federal Trademark Experts"
      />
    </div>
  );
};

export default Home;